import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Eye,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
  CheckCircle,
  Users,
  Download,
  RefreshCw,
  X,
} from 'lucide-react';
import { api, type EntityListItem } from '../services/api';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { SearchInput } from '../components/ui/SearchInput';
import { Select } from '../components/ui/Select';
import { Pagination } from '../components/ui/Pagination';
import { SkeletonTable } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Alert } from '../components/ui/Alert';

type SortBy = 'entityId' | 'name' | 'lastActivity';
type SortDir = 'asc' | 'desc';

export default function EntityListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [dapOnly, setDapOnly] = useState(searchParams.get('dapOnly') === 'true');
  const [dataSource, setDataSource] = useState('');
  const [matchLevel, setMatchLevel] = useState<'possible' | 'possibly_related' | 'resolved' | ''>('');
  const [sortBy, setSortBy] = useState<SortBy>('entityId');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedEntities, setSelectedEntities] = useState<number[]>([]);

  // Fetch entities
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['entity-list', page, search, dapOnly, dataSource, matchLevel, sortBy, sortDir],
    queryFn: () =>
      api.listEntities({
        page,
        limit: 50,
        search: search || undefined,
        dapOnly: dapOnly || undefined,
        dataSource: dataSource || undefined,
        matchLevel: matchLevel || undefined,
        sortBy,
        sortDir,
      }),
  });

  // Fetch data sources for filter
  const { data: dataSourcesData } = useQuery({
    queryKey: ['data-sources'],
    queryFn: () => api.getDataSources(),
  });

  const handleSort = (column: SortBy) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
    setPage(1);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setDapOnly(false);
    setDataSource('');
    setMatchLevel('');
    setPage(1);
    setSearchParams({});
  };

  const hasActiveFilters = search || dapOnly || dataSource || matchLevel;

  const toggleSelectAll = () => {
    if (selectedEntities.length === data?.entities.length) {
      setSelectedEntities([]);
    } else {
      setSelectedEntities(data?.entities.map(e => e.entityId) || []);
    }
  };

  const toggleSelect = (entityId: number) => {
    setSelectedEntities(prev =>
      prev.includes(entityId)
        ? prev.filter(id => id !== entityId)
        : [...prev, entityId]
    );
  };

  const SortIcon = ({ column }: { column: SortBy }) => {
    if (sortBy !== column) return null;
    return sortDir === 'asc' ? (
      <ChevronUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 inline ml-1" />
    );
  };

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 30) return `${Math.floor(diffDays / 30)}mo ago`;
    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    return 'Just now';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Entities</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {data?.pagination.totalCount.toLocaleString() || '0'} resolved entities
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Download className="h-4 w-4" />}
          >
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card padding="none">
        <CardContent className="!mt-0 p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[240px]">
              <SearchInput
                value={search}
                onChange={setSearch}
                onSearch={handleSearch}
                placeholder="Search by name, email, or ID..."
                isLoading={isFetching}
              />
            </div>

            {/* Data Source Filter */}
            <Select
              value={dataSource}
              onChange={(e) => {
                setDataSource(e.target.value);
                setPage(1);
              }}
              options={[
                { value: '', label: 'All Sources' },
                ...(dataSourcesData?.dataSources.map((ds) => ({
                  value: ds.code,
                  label: ds.code,
                })) || []),
              ]}
              className="w-40"
            />

            {/* Match Level Filter */}
            <Select
              value={matchLevel}
              onChange={(e) => {
                setMatchLevel(e.target.value as 'possible' | 'possibly_related' | 'resolved' | '');
                setPage(1);
              }}
              options={[
                { value: '', label: 'All Levels' },
                { value: 'possible', label: 'Possible Match' },
                { value: 'possibly_related', label: 'Possibly Related' },
                { value: 'resolved', label: 'Resolved' },
              ]}
              className="w-48"
            />

            {/* DAP Filter */}
            <button
              onClick={() => {
                setDapOnly(!dapOnly);
                setPage(1);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
                dapOnly
                  ? 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300'
                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
            >
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm font-medium">DAP Only</span>
            </button>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<X className="h-4 w-4" />}
                onClick={clearFilters}
              >
                Clear
              </Button>
            )}
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-500 dark:text-gray-400">Active filters:</span>
              {search && (
                <Badge variant="info" size="sm">
                  Search: "{search}"
                </Badge>
              )}
              {dataSource && (
                <Badge variant="info" size="sm">
                  Source: {dataSource}
                </Badge>
              )}
              {matchLevel && (
                <Badge variant="info" size="sm">
                  Level: {matchLevel}
                </Badge>
              )}
              {dapOnly && (
                <Badge variant="danger" size="sm">
                  DAP Only
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <SkeletonTable rows={10} columns={7} />
      ) : error ? (
        <Alert variant="danger" title="Error loading entities">
          Failed to fetch entity data. Please try again.
        </Alert>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <th className="px-4 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      checked={selectedEntities.length === data?.entities.length && data?.entities.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                    onClick={() => handleSort('entityId')}
                  >
                    Entity ID <SortIcon column="entityId" />
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    Name <SortIcon column="name" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Match Level
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Data Sources
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    DAP Status
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                    onClick={() => handleSort('lastActivity')}
                  >
                    Last Activity <SortIcon column="lastActivity" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {data?.entities.map((entity) => (
                  <EntityRow
                    key={entity.entityId}
                    entity={entity}
                    formatRelativeTime={formatRelativeTime}
                    isSelected={selectedEntities.includes(entity.entityId)}
                    onToggleSelect={() => toggleSelect(entity.entityId)}
                  />
                ))}
              </tbody>
            </table>

            {data?.entities.length === 0 && (
              <EmptyState
                icon={<Users className="h-8 w-8 text-gray-400" />}
                title="No entities found"
                description="Try adjusting your filters or search criteria"
                action={
                  hasActiveFilters && (
                    <Button variant="secondary" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  )
                }
              />
            )}
          </div>

          {/* Pagination */}
          {data?.pagination && data.pagination.totalPages > 1 && (
            <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing {((page - 1) * 50) + 1} to {Math.min(page * 50, data.pagination.totalCount)} of{' '}
                {data.pagination.totalCount.toLocaleString()} results
              </div>
              <Pagination
                currentPage={page}
                totalPages={data.pagination.totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </Card>
      )}
    </motion.div>
  );
}

function EntityRow({
  entity,
  formatRelativeTime,
  isSelected,
  onToggleSelect,
}: {
  entity: EntityListItem;
  formatRelativeTime: (date: string) => string;
  isSelected: boolean;
  onToggleSelect: () => void;
}) {
  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
        isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
      }`}
    >
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
        />
      </td>
      <td className="px-4 py-3">
        <span className="text-sm font-mono text-gray-900 dark:text-gray-100">
          {entity.entityId}
        </span>
      </td>
      <td className="px-4 py-3">
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{entity.name}</div>
          {entity.email && (
            <div className="text-xs text-gray-500 dark:text-gray-400">{entity.email}</div>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <MatchLevelBadge level={entity.matchLevel} />
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {entity.dataSources.length > 0 ? (
            entity.dataSources.slice(0, 3).map((ds) => (
              <Badge key={ds} variant="default" size="sm">
                {ds}
              </Badge>
            ))
          ) : (
            <span className="text-xs text-gray-400">None</span>
          )}
          {entity.dataSources.length > 3 && (
            <span className="text-xs text-gray-500 dark:text-gray-400 self-center">
              +{entity.dataSources.length - 3}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <DapStatus hasDap={entity.hasDap} isConfirmed={entity.isDapConfirmed} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
        {formatRelativeTime(entity.lastActivity)}
      </td>
      <td className="px-4 py-3">
        <Link
          to={`/entities/${entity.entityId}`}
          className="inline-flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <Eye className="h-5 w-5" />
        </Link>
      </td>
    </motion.tr>
  );
}

function MatchLevelBadge({ level }: { level: string }) {
  const variants: Record<string, 'success' | 'warning' | 'info' | 'purple' | 'default'> = {
    'Possible Match': 'warning',
    Resolved: 'success',
    'Possibly Related': 'info',
    Disclosed: 'purple',
  };

  return <Badge variant={variants[level] || 'default'}>{level}</Badge>;
}

function DapStatus({ hasDap, isConfirmed }: { hasDap: boolean; isConfirmed: boolean }) {
  if (!hasDap) {
    return <span className="text-xs text-gray-400 dark:text-gray-500">-</span>;
  }

  if (isConfirmed) {
    return (
      <Badge variant="danger" icon={<CheckCircle className="h-3 w-3" />}>
        Confirmed
      </Badge>
    );
  }

  return (
    <Badge variant="warning" icon={<AlertTriangle className="h-3 w-3" />}>
      Suspected
    </Badge>
  );
}
